import { z } from 'zod';
import { RECHTSTEXT_DOKUMENTE, type RechtstextDokument } from './portal';
import { RECHTSTEXT_MAX_ZEICHEN } from './rechtstexte';

/**
 * Plattform → Einstellungen → Rechtstexte: Dokumente von DXL gegenüber den Marktplatz-Kunden
 * (AGB/Plattformvertrag, AVV, Datenschutz, Impressum) und die Vorlagen für die Rechtstexte der Marktplätze.
 * Jede gespeicherte Fassung ist eine neue Version (Entwurf); erst das Veröffentlichen macht sie gültig.
 */

export const DXL_RECHTSTEXTE = {
  dxl_agb: 'AGB / Plattformvertrag',
  dxl_avv: 'Auftragsverarbeitungsvertrag (AVV)',
  dxl_datenschutz: 'Datenschutzerklärung von DXL',
  dxl_impressum: 'Impressum von DXL',
} as const;
export type DxlRechtstext = keyof typeof DXL_RECHTSTEXTE;
export const DXL_RECHTSTEXT_IDS = Object.keys(DXL_RECHTSTEXTE) as [DxlRechtstext, ...DxlRechtstext[]];

/** Dokumente, denen die Inhaber der Marktplätze in der aktuellen Fassung zustimmen müssen */
export const DXL_ZUSTIMMUNG_DOKUMENTE = ['dxl_agb', 'dxl_avv'] as const satisfies readonly DxlRechtstext[];
export type DxlZustimmungDokument = (typeof DXL_ZUSTIMMUNG_DOKUMENTE)[number];

export type VorlageDokument = `vorlage_${RechtstextDokument}`;
export const vorlageId = (d: RechtstextDokument): VorlageDokument => `vorlage_${d}`;
export const VORLAGE_IDS = (Object.keys(RECHTSTEXT_DOKUMENTE) as RechtstextDokument[]).map(vorlageId);

export type PlattformDokument = DxlRechtstext | VorlageDokument;
export const PLATTFORM_DOKUMENT_IDS = [...DXL_RECHTSTEXT_IDS, ...VORLAGE_IDS] as [PlattformDokument, ...PlattformDokument[]];
export const PlattformDokumentSchema = z.enum(PLATTFORM_DOKUMENT_IDS);

export const istVorlage = (d: PlattformDokument): d is VorlageDokument => d.startsWith('vorlage_');
/** vorlage_haendlervertrag → haendlervertrag */
export const vorlageFuer = (d: VorlageDokument): RechtstextDokument => d.slice('vorlage_'.length) as RechtstextDokument;

export function plattformDokumentTitel(d: PlattformDokument): string {
  return istVorlage(d) ? `Vorlage: ${RECHTSTEXT_DOKUMENTE[vorlageFuer(d)]}` : DXL_RECHTSTEXTE[d];
}

export const PLATTFORM_DOKUMENT_FUER: Record<PlattformDokument, string> = {
  dxl_agb: 'Marktplatz-Kunden – Zustimmung der Inhaber erforderlich',
  dxl_avv: 'Marktplatz-Kunden – Zustimmung der Inhaber erforderlich',
  dxl_datenschutz: 'Marktplatz-Kunden und Besucher der Plattform',
  dxl_impressum: 'Öffentlich – Anbieterkennzeichnung von DXL',
  vorlage_haendlervertrag: 'Startfassung des Händlervertrags für Marktplätze',
  vorlage_agb: 'Startfassung der AGB für Käufer im Marktplatz-Shop',
  vorlage_datenschutz: 'Startfassung der Datenschutzerklärung (Shop, Händlerportal, App)',
};

export const PlattformVorschauSchema = z.object({ html: z.string().max(RECHTSTEXT_MAX_ZEICHEN * 2).optional() });
export const PlattformVeroeffentlichenSchema = z.object({ version: z.string().regex(/^\d{1,4}\.\d{1,6}$/) });

export interface PlattformRechtstextVersion {
  version: string;
  stand: string;
  sha256: string;
  erstelltAm: string | null;
  von: string;
  veroeffentlichtAm: string | null;
}

export interface PlattformRechtstextFassung extends PlattformRechtstextVersion {
  dokument: PlattformDokument;
  titel: string;
  html: string;
  /** Vorlage aus dem Programmcode (Version 1.0, noch nie überarbeitet) */
  mitgeliefert: boolean;
}

export interface PlattformRechtstextInfo {
  dokument: PlattformDokument;
  titel: string;
  fuer: string;
  art: 'dxl' | 'vorlage';
  /** gültige Fassung – null, solange nichts veröffentlicht ist */
  veroeffentlicht: PlattformRechtstextVersion | null;
  /** neuere, noch nicht veröffentlichte Fassung */
  entwurf: PlattformRechtstextVersion | null;
  versionen: number;
  /** öffentliche Adresse (nur Dokumente von DXL) */
  url: string | null;
  /** AGB und AVV: Marktplätze mit Zustimmung zur gültigen Fassung */
  zustimmungen: { aktuell: number; gesamt: number } | null;
  /** Vorlagen: Marktplätze, deren Rechtstext auf der gültigen Vorlage beruht */
  marktplaetze: { aktuell: number; gesamt: number } | null;
}

export interface PlattformRechtstextUebersicht {
  dokumente: PlattformRechtstextInfo[];
}

export interface PlattformRechtstextVersionSeite {
  eintraege: PlattformRechtstextVersion[];
  weiter: string | null;
}

export interface PlattformRechtstextSpeichernAntwort {
  fassung: PlattformRechtstextFassung;
  unveraendert: boolean;
}

/** Gerenderte Fassung für Vorschau und öffentliche Adresse */
export interface PlattformRechtstextAnsicht {
  dokument: PlattformDokument;
  titel: string;
  html: string;
  version: string;
  stand: string;
  sha256: string;
}

export interface PlattformZustimmungEintrag {
  id: string;
  mandant: { id: string; kuerzel: string; name: string };
  dokument: DxlZustimmungDokument;
  version: string;
  sha256: string;
  am: string;
  von: string;
}

export interface PlattformZustimmungSeite {
  eintraege: PlattformZustimmungEintrag[];
  weiter: string | null;
}

/* ------------------------------------------------------------------ Marktplatz-Konto */

export interface DxlRechtstextKonto {
  dokument: DxlRechtstext;
  titel: string;
  version: string;
  stand: string;
  url: string;
  /** nur AGB und AVV */
  zustimmungNoetig: boolean;
  /** Zustimmung zur gültigen Fassung: Zeitpunkt und Nutzer */
  zugestimmt: { am: string; von: string; version: string } | null;
  /** zuvor einer älteren Fassung zugestimmt */
  neueFassung: boolean;
}

export interface DxlRechtstexteKonto {
  /** Inhaber außerhalb der DXL-Ansicht – nur dann ist die Zustimmung möglich */
  darfZustimmen: boolean;
  dokumente: DxlRechtstextKonto[];
  /** Dokumente, denen der Marktplatz noch zustimmen muss */
  offen: DxlZustimmungDokument[];
}

export const DxlZustimmenSchema = z.object({
  dokumente: z
    .array(z.object({ dokument: z.enum(DXL_ZUSTIMMUNG_DOKUMENTE), version: z.string().regex(/^\d{1,4}\.\d{1,6}$/) }))
    .min(1)
    .max(DXL_ZUSTIMMUNG_DOKUMENTE.length),
});
export type DxlZustimmenEingabe = z.infer<typeof DxlZustimmenSchema>;
